import React, { useEffect } from 'react';
import {
  PageWithHeader,
  Content,
  ContentHeader,
  CreateButton,
  SupportButton,
  Progress,
  ResponseErrorPanel,
  TableColumn,
  TableProps,
} from '@backstage/core-components';
import { useApi, configApiRef, useRouteRef } from '@backstage/core-plugin-api';
import { CatalogTable, CatalogTableRow } from '@backstage/plugin-catalog';
import {
  CatalogFilterLayout,
  DefaultEntityFilters,
  EntityKindPicker,
  EntityLifecyclePicker,
  EntityListPagination,
  EntityListProvider,
  EntityOwnerPicker,
  EntityOwnerPickerProps,
  EntityTagPicker,
  EntityTypePicker,
  useEntityList,
  UserListFilterKind,
  UserListPicker,
} from '@backstage/plugin-catalog-react';
import { usePermission } from '@backstage/plugin-permission-react';
import { catalogEntityCreatePermission } from '@backstage/plugin-catalog-common/alpha';
import { apiDocsPlugin } from '@backstage/plugin-api-docs';
import {
  AllowedSystemsEntityFilter,
  useAllowedSystemsEntityFilter,
} from '../../hooks/useAllowedSystemsEntityFilter';

type AllowedSystemsApiExplorerPageProps = {
  initiallySelectedFilter?: UserListFilterKind;
  columns?: TableColumn<CatalogTableRow>[];
  actions?: TableProps<CatalogTableRow>['actions'];
  ownerPickerMode?: EntityOwnerPickerProps['mode'];
  pagination?: EntityListPagination;
};

type ApiExplorerEntityFilters = DefaultEntityFilters & {
  allowedSystems?: AllowedSystemsEntityFilter;
};

const AllowedSystemsFilterConfigurator = ({
  filter,
}: {
  filter?: AllowedSystemsEntityFilter;
}) => {
  const { updateFilters } = useEntityList<ApiExplorerEntityFilters>();

  useEffect(() => {
    updateFilters(() => ({ allowedSystems: filter }));
  }, [filter, updateFilters]);

  return null;
};

const defaultColumns = [
  CatalogTable.columns.createTitleColumn({ hidden: true }),
  CatalogTable.columns.createNameColumn({ defaultKind: 'API' }),
  CatalogTable.columns.createSystemColumn(),
  CatalogTable.columns.createOwnerColumn(),
  CatalogTable.columns.createSpecTypeColumn(),
  CatalogTable.columns.createSpecLifecycleColumn(),
  CatalogTable.columns.createMetadataDescriptionColumn(),
  CatalogTable.columns.createTagsColumn(),
];

export const AllowedSystemsApiExplorerPage = (
  props: AllowedSystemsApiExplorerPageProps,
) => {
  const {
    initiallySelectedFilter = 'all',
    columns,
    actions,
    ownerPickerMode,
    pagination,
  } = props;

  const configApi = useApi(configApiRef);
  const registerComponentLink = useRouteRef(
    apiDocsPlugin.externalRoutes.registerApi,
  );
  const { allowed } = usePermission({ permission: catalogEntityCreatePermission });

  const {
    filter: allowedSystemsFilter,
    loading: userLoading,
    error: userError,
  } = useAllowedSystemsEntityFilter();

  const generatedSubtitle = `${
    configApi.getOptionalString('organization.name') ?? 'Backstage'
  } API Explorer`;

  const renderContent = () => {
    if (userLoading) {
      return <Progress />;
    }

    if (userError) {
      return <ResponseErrorPanel error={userError} />;
    }

    return (
      <EntityListProvider<ApiExplorerEntityFilters> pagination={pagination}>
        <AllowedSystemsFilterConfigurator filter={allowedSystemsFilter} />
        <CatalogFilterLayout>
          <CatalogFilterLayout.Filters>
            <EntityKindPicker initialFilter="api" hidden />
            <EntityTypePicker />
            <UserListPicker initialFilter={initiallySelectedFilter} />
            <EntityOwnerPicker mode={ownerPickerMode} />
            <EntityLifecyclePicker />
            <EntityTagPicker />
          </CatalogFilterLayout.Filters>
          <CatalogFilterLayout.Content>
            <CatalogTable columns={columns ?? defaultColumns} actions={actions} />
          </CatalogFilterLayout.Content>
        </CatalogFilterLayout>
      </EntityListProvider>
    );
  };

  return (
    <PageWithHeader
      themeId="apis"
      title="APIs"
      subtitle={generatedSubtitle}
      pageTitleOverride="APIs"
    >
      <Content>
        <ContentHeader title="">
          {allowed && (
            <CreateButton title="Register Existing API" to={registerComponentLink?.()} />
          )}
          <SupportButton>All your APIs</SupportButton>
        </ContentHeader>
        {renderContent()}
      </Content>
    </PageWithHeader>
  );
};
